import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Typography from 'carbon-react/lib/components/typography';

import {
  AnalysisFeedbackDraft,
  AnalysisFeedbackRating,
  AnalysisFeedbackSaveResult
} from './components/analysis-feedback/analysis-feedback-types';
import { AnalysisFeedbackSidebar } from './components/analysis-feedback/analysis-feedback-sidebar';
import { BusinessHealthCard } from './components/business-health-card/business-health-card';
import { CostStructureCard } from './components/cost-structure-card/cost-structure-card';
import { InsightsModal } from './components/insights-modal/insights-modal';
import { LiquidityOverviewCard } from './components/liquidity-overview-card/liquidity-overview-card';
import { MetricCardsSection } from './components/metric-cards-section/metric-cards-section';
import { RevenueTrendChart } from './components/revenue-trend-chart/revenue-trend-chart';
import { useAnalysisFeedback } from './hooks/use-analysis-feedback';
import {
  MappedCostStructure,
  MappedExecutiveSummary,
  MappedHealthScore,
  MappedHighlightMetric,
  MappedInsight,
  MappedKpiMetric,
  MappedLiquidity,
  MappedRevenueChart
} from './mappers/types';

import * as styles from './ai-insights.scss';

export type InsightsVariant = 'accountant' | 'businessOwner';

export interface AiInsightsData {
  analysisId: string;
  analysisVersion: number | null;
  periodLabel?: string;
  createdAt?: string;
  healthScore?: MappedHealthScore;
  kpiMetrics?: MappedKpiMetric[];
  highlightMetrics?: MappedHighlightMetric[];
  revenueChart?: MappedRevenueChart;
  costStructure?: MappedCostStructure;
  liquidity?: MappedLiquidity;
  insights: MappedInsight[];
  executiveSummary: MappedExecutiveSummary;
}

interface AiInsightsProps {
  organisationId: string;
  onAnalysisRefresh: () => Promise<void> | void;
  variant: InsightsVariant;
  data: AiInsightsData;
  workspaceId: string;
}

export const AiInsights = ({ data, onAnalysisRefresh, organisationId, variant, workspaceId }: AiInsightsProps) => {
  const intl = useIntl();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [initialFeedbackRating, setInitialFeedbackRating] = useState<AnalysisFeedbackRating | null>(null);
  const [selectedFeedbackRating, setSelectedFeedbackRating] = useState<AnalysisFeedbackRating | null>(null);
  const {
    feedback,
    isAccessDenied,
    isLoading: isFeedbackLoading,
    loadError: feedbackLoadError,
    loadFeedback,
    saveFeedback
  } = useAnalysisFeedback(workspaceId, organisationId, data.analysisId);

  useEffect(() => {
    setIsFeedbackOpen(false);
    setInitialFeedbackRating(null);
    setSelectedFeedbackRating(null);
  }, [data.analysisId, data.analysisVersion]);

  useEffect(() => {
    if (isAccessDenied) {
      setIsFeedbackOpen(false);
    }
  }, [isAccessDenied]);

  const handleFeedbackOpen = useCallback(
    (rating?: AnalysisFeedbackRating) => {
      setInitialFeedbackRating(rating ?? null);
      if (rating) {
        setSelectedFeedbackRating(rating);
      }
      setIsFeedbackOpen(true);
      void loadFeedback();
    },
    [loadFeedback]
  );

  const handleFeedbackSave = useCallback(
    async (draft: AnalysisFeedbackDraft): Promise<AnalysisFeedbackSaveResult> => {
      if (!draft.rating) {
        throw new Error('A feedback rating is required');
      }

      const result = await saveFeedback({
        comment: draft.comment,
        rating: draft.rating,
        reasons: draft.reasons
      });

      if (result.status === 'saved') {
        setSelectedFeedbackRating(draft.rating);
      } else if (result.status === 'stale_analysis') {
        void onAnalysisRefresh();
      }

      return result;
    },
    [onAnalysisRefresh, saveFeedback]
  );

  return (
    <div className={styles.aiInsights}>
      <InsightsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} variant={variant} data={data} />
      <AnalysisFeedbackSidebar
        feedback={feedback}
        initialRating={initialFeedbackRating}
        isLoading={isFeedbackLoading}
        isOpen={isFeedbackOpen}
        loadError={feedbackLoadError}
        onClose={() => setIsFeedbackOpen(false)}
        onRetryLoad={loadFeedback}
        onSave={handleFeedbackSave}
      />

      {data.healthScore && (
        <div className={styles.healthCardWrapper}>
          <BusinessHealthCard
            score={data.healthScore.score}
            overallStatus={data.healthScore.overallStatus}
            title={data.healthScore.title}
            description={data.healthScore.description}
            statusIndicators={data.healthScore.statusIndicators}
            createdAt={data.createdAt}
            feedbackEnabled={!isAccessDenied}
            hasFeedback={!!feedback}
            onFeedbackOpen={handleFeedbackOpen}
            onViewInsights={() => setIsModalOpen(true)}
            selectedFeedbackRating={selectedFeedbackRating}
          />
        </div>
      )}

      <Box mt={4}>
        <hr className={styles.overviewDivider} />
        <Box mb={3}>
          <Typography fontSize="24px" fontWeight="700" lineHeight="125%" mb={0}>
            {intl.formatMessage({ id: 'predict.overview', defaultMessage: 'Overview' })}
          </Typography>
        </Box>

        <MetricCardsSection variant={variant} kpiMetrics={data.kpiMetrics} highlightMetrics={data.highlightMetrics} />

        {data.revenueChart && data.revenueChart.months.length > 0 && (
          <Box mb={3}>
            <RevenueTrendChart
              title={data.revenueChart.title}
              subtitle={data.revenueChart.subtitle}
              months={data.revenueChart.months}
              actuals={data.revenueChart.actuals}
              forecast={data.revenueChart.forecast}
              yAxisPrefix={data.revenueChart.yAxisPrefix}
              warningMessage={data.revenueChart.warningMessage}
            />
          </Box>
        )}

        <Box display="flex" gap="24px" alignItems="stretch">
          {data.costStructure && (
            <Box flex="1">
              <CostStructureCard
                title={data.costStructure.title}
                subtitle={data.costStructure.subtitle}
                totalLabel={data.costStructure.totalLabel}
                segments={data.costStructure.segments}
                formatValue={data.costStructure.formatValue}
                formatTotal={data.costStructure.formatTotal}
                message={data.costStructure.message}
              />
            </Box>
          )}
          {data.liquidity && data.liquidity.metrics.length > 0 && (
            <Box flex="1">
              <LiquidityOverviewCard
                title={data.liquidity.title}
                subtitle={data.liquidity.subtitle}
                metrics={data.liquidity.metrics}
                message={data.liquidity.message}
              />
            </Box>
          )}
        </Box>
      </Box>
    </div>
  );
};
